import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

const ENTITY_TYPES = [
  { value: 'employee', label: 'Employee' },
  { value: 'company', label: 'Company' },
  { value: 'facility', label: 'Facility' },
  { value: 'truck', label: 'Truck' },
];

const ENTITY_COLORS = {
  employee: '#3b82f6', company: '#8b5cf6', facility: '#f59e0b', truck: '#10b981',
};

const EMPTY_ITEM = { category_id: '', title: '', description: '', is_required: true, reminder_days: '30,7,1' };

export default function ComplianceTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState('employee');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tmpl, cats] = await Promise.all([
        api.getComplianceTemplates(),
        api.getComplianceCategories(),
      ]);
      setTemplates(Array.isArray(tmpl) ? tmpl : []);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (err) {
      setError(err.message || 'Failed to load');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setEditing(null);
    setName('');
    setEntityType('employee');
    setDescription('');
    setItems([{ ...EMPTY_ITEM }]);
    setError('');
    setShowModal(true);
  }

  async function openEdit(tmpl) {
    setError('');
    try {
      const full = await api.getComplianceTemplate(tmpl.id);
      setEditing(full);
      setName(full.name);
      setEntityType(full.entity_type);
      setDescription(full.description || '');
      setItems(full.items && full.items.length > 0
        ? full.items.map(i => ({
            category_id: i.category_id || '',
            title: i.title || '',
            description: i.description || '',
            is_required: !!i.is_required,
            reminder_days: i.reminder_days || '30,7,1',
          }))
        : [{ ...EMPTY_ITEM }]
      );
      setShowModal(true);
    } catch (err) {
      setError(err.message || 'Failed to load template');
    }
  }

  function addItem() {
    setItems(prev => [...prev, { ...EMPTY_ITEM }]);
  }

  function removeItem(idx) {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }

  function updateItem(idx, field, value) {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  }

  async function handleSave() {
    if (!name.trim()) { setError('Template name is required'); return; }
    const validItems = items.filter(i => i.title.trim() && i.category_id);
    if (validItems.length === 0) { setError('Add at least one item with title and category'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = { name: name.trim(), entity_type: entityType, description: description.trim() || null, items: validItems };
      if (editing) {
        await api.updateComplianceTemplate(editing.id, payload);
        setSuccess('Template updated');
      } else {
        await api.createComplianceTemplate(payload);
        setSuccess('Template created');
      }
      setShowModal(false);
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    try {
      await api.deleteComplianceTemplate(id);
      setDeleteConfirm(null);
      load();
      setSuccess('Template deactivated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      setDeleteConfirm(null);
    }
  }

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Compliance Templates</h1>
        <button className="btn btn-gold" onClick={openAdd}>+ New Template</button>
      </div>

      <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>
        Templates pre-define compliance items for an entity type. Apply a template to auto-create items instead of adding them one by one.
      </p>

      {error && !showModal && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? (
        <div className="loading-spinner">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
          No templates yet. Create one to get started.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {templates.map(tmpl => (
            <div key={tmpl.id} style={{
              background: '#fff', borderRadius: '12px', padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0',
              borderLeft: `4px solid ${ENTITY_COLORS[tmpl.entity_type] || '#94a3b8'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>{tmpl.name}</h3>
                  {tmpl.description && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>{tmpl.description}</p>}
                </div>
                <span style={{
                  fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                  padding: '2px 8px', borderRadius: '4px',
                  background: `${ENTITY_COLORS[tmpl.entity_type]}15`,
                  color: ENTITY_COLORS[tmpl.entity_type],
                }}>{tmpl.entity_type}</span>
              </div>
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                {tmpl.item_count} item{tmpl.item_count !== 1 ? 's' : ''}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-sm btn-outline" onClick={() => openEdit(tmpl)}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(tmpl)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal title={editing ? 'Edit Template' : 'New Template'} onClose={() => setShowModal(false)} wide>
          {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

          <div className="form-grid">
            <div className="form-group">
              <label>Template Name *</label>
              <input value={name} onChange={e => setName(e.target.value)} className="form-input" placeholder="e.g. New Driver Checklist" />
            </div>
            <div className="form-group">
              <label>Entity Type *</label>
              <select value={entityType} onChange={e => setEntityType(e.target.value)} className="form-input" disabled={!!editing}>
                {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)} className="form-input" placeholder="What is this template for?" />
          </div>

          <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>Template Items</h3>
              <button className="btn btn-sm btn-outline" onClick={addItem}>+ Add Item</button>
            </div>

            {items.map((item, idx) => (
              <div key={idx} style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px',
                padding: '12px', background: '#f8fafc', borderRadius: '8px',
                marginBottom: '8px', alignItems: 'start',
              }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Title *</label>
                  <input
                    value={item.title}
                    onChange={e => updateItem(idx, 'title', e.target.value)}
                    className="form-input"
                    placeholder="e.g. CDL Class A"
                    style={{ fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Category *</label>
                  <select
                    value={item.category_id}
                    onChange={e => updateItem(idx, 'category_id', e.target.value)}
                    className="form-input"
                    style={{ fontSize: '13px' }}
                  >
                    <option value="">Select...</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div style={{ paddingTop: '18px' }}>
                  <button
                    className="btn btn-sm"
                    onClick={() => removeItem(idx)}
                    disabled={items.length <= 1}
                    style={{ color: '#ef4444', padding: '4px 8px' }}
                    title="Remove item"
                  >&times;</button>
                </div>

                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#475569' }}>
                    <input
                      type="checkbox"
                      checked={item.is_required}
                      onChange={e => updateItem(idx, 'is_required', e.target.checked)}
                    />
                    Required
                  </label>
                  <div style={{ flex: 1 }}>
                    <input
                      value={item.reminder_days}
                      onChange={e => updateItem(idx, 'reminder_days', e.target.value)}
                      className="form-input"
                      placeholder="30,7,1"
                      style={{ fontSize: '12px' }}
                    />
                  </div>
                  <div style={{ flex: 2 }}>
                    <input
                      value={item.description || ''}
                      onChange={e => updateItem(idx, 'description', e.target.value)}
                      className="form-input"
                      placeholder="Description (optional)"
                      style={{ fontSize: '12px' }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="btn btn-gold" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : (editing ? 'Update Template' : 'Create Template')}
            </button>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <Modal title="Delete Template" onClose={() => setDeleteConfirm(null)}>
          <p>Deactivate <strong>{deleteConfirm.name}</strong>?</p>
          <p style={{ fontSize: '13px', color: '#64748b' }}>This won't affect compliance items already created from this template.</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Deactivate</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
